import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import Logo from '../components/Logo';
import { toast } from 'sonner';
import axios from 'axios';
import { User, TrendingUp, Target, Briefcase, GraduationCap, Award, Clock, ChevronRight, Users, BookOpen, BarChart3, AlertTriangle, Star, UserPlus, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ParentDashboardPage() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [linkedStudents, setLinkedStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resumingSetup, setResumingSetup] = useState(false);
  const [error, setError] = useState(null);

  const handleResumeChildSetup = async () => {
    if (resumingSetup) return;
    setResumingSetup(true);
    try {
      const res = await axios.post(`${API}/family/resume-child-setup`, {}, getAuthHeader());
      if (res.data?.setup_link) {
        // Internal link, strip the host so React Router handles it
        const path = res.data.setup_link.replace(/^https?:\/\/[^/]+/, '');
        navigate(path);
      } else {
        toast.error('Could not start child setup. Please try again.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start child setup.');
    } finally {
      setResumingSetup(false);
    }
  };

  useEffect(() => {
    fetchLinkedStudents();
  }, []);

  useEffect(() => {
    if (selectedStudent) {
      fetchStudentDashboard(selectedStudent.id);
    }
  }, [selectedStudent]);

  const fetchLinkedStudents = async () => {
    try {
      const response = await axios.get(`${API}/family/linked-students`, getAuthHeader());
      setLinkedStudents(response.data.students || []);
      if (response.data.students?.length > 0) {
        setSelectedStudent(response.data.students[0]);
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch linked students:', err);
      setError('Failed to load linked students');
      setLoading(false);
    }
  };

  const fetchStudentDashboard = async (studentId) => {
    try {
      const response = await axios.get(`${API}/parent/dashboard/${studentId}`, getAuthHeader());
      setStudentData(response.data);
    } catch (err) {
      console.error('Failed to fetch student dashboard:', err);
      setError('Failed to load student data');
    }
    // Also fetch portfolio summary
    try {
      const portfolioRes = await axios.get(`${API}/portfolio/child/${studentId}/summary`, getAuthHeader());
      setPortfolioData(portfolioRes.data);
    } catch {
      setPortfolioData(null);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </AppLayout>
    );
  }

  if (linkedStudents.length === 0) {
    const numKids = user?.num_children_planned || 1;
    return (
      <AppLayout>
        <div className="max-w-3xl mx-auto py-12 px-4" data-testid="parent-empty-state">
          {/* Brand lockup */}
          <div className="flex flex-col items-center mb-8">
            <Logo size="lg" />
            <p className="text-xs text-slate-300 tracking-[0.2em] mt-3 uppercase">Agentic FuturePath AI · Family Plan</p>
          </div>

          {/* Resume Child Setup hero */}
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-2xl p-8 mb-6">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 border border-blue-500/40 flex items-center justify-center shrink-0">
                <UserPlus className="w-6 h-6 text-blue-400" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white mb-1.5">Welcome back, {user?.full_name?.split(' ')[0] || 'Parent'}</h1>
                <p className="text-slate-300 text-sm leading-relaxed">
                  Your <strong className="text-white">Family Plan</strong> is paid and active.
                  You haven't finished registering your child yet, we left off right where you can pick back up.
                </p>
              </div>
            </div>

            <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 mb-5">
              <p className="text-xs text-slate-300 uppercase tracking-wider mb-2">What's left</p>
              <ul className="space-y-1.5 text-sm text-slate-200">
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> Enter child information ({numKids} child{numKids > 1 ? 'ren' : ''})</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> Sign 4 consent documents per child</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> Receive child's login credentials by email</li>
              </ul>
              <p className="text-xs text-slate-300 mt-3">Estimated time: 7 minutes per child</p>
            </div>

            <button
              onClick={handleResumeChildSetup}
              disabled={resumingSetup}
              data-testid="resume-child-setup-btn"
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-blue-500/20"
            >
              {resumingSetup ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Preparing your setup...</>
              ) : (
                <>Resume Child Registration <ChevronRight className="w-4 h-4" /></>
              )}
            </button>

            <p className="text-xs text-slate-300 text-center mt-3">
              No additional charge. We're picking up exactly where you stopped.
            </p>
          </div>

          {/* Already have a linked child? */}
          <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
            <p className="text-xs text-slate-300 uppercase tracking-wider mb-2">Or, link an existing student</p>
            <p className="text-sm text-slate-300 mb-1">If your child already has a Agentic FuturePath AI account from a previous registration:</p>
            <ol className="space-y-1 text-xs text-slate-300 list-decimal list-inside">
              <li>Have them log in to their account</li>
              <li>Go to Settings → Family &amp; Sharing</li>
              <li>Send the invitation to <span className="font-mono text-slate-300">{user?.email}</span></li>
            </ol>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Parent Dashboard</h1>
          <p className="text-gray-400">Monitor your child's career planning progress</p>
        </div>

        {/* Student Selector (if multiple students) */}
        {linkedStudents.length > 1 && (
          <div className="mb-6">
            <label className="text-sm text-gray-400 mb-2 block">Select Student</label>
            <div className="flex gap-3 flex-wrap">
              {linkedStudents.map(student => (
                <button
                  key={student.id}
                  onClick={() => setSelectedStudent(student)}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    selectedStudent?.id === student.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-navy-800 text-gray-300 hover:bg-navy-700'
                  }`}
                >
                  <User className="w-4 h-4" />
                  {student.full_name}
                </button>
              ))}
            </div>
          </div>
        )}

        {studentData && (
          <>
            {/* Student Overview Card */}
            <div className="bg-navy-800/50 rounded-xl p-6 mb-6 border border-navy-700">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center">
                    <User className="w-8 h-8 text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">{studentData.student.full_name}</h2>
                    <p className="text-gray-400">
                      {studentData.student.grade_level ? `Grade ${studentData.student.grade_level}` : 'High School Student'}
                      {studentData.student.city && ` • ${studentData.student.city}, ${studentData.student.state}`}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Linked as {studentData.relationship} since {new Date(studentData.linked_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-400">Profile Strength</div>
                  <div className="text-3xl font-bold text-blue-400">{studentData.profile_strength?.percentage || 0}%</div>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard
                icon={<Target className="w-5 h-5" />}
                label="AI Actions"
                value={studentData.usage_stats?.total_ai_actions || 0}
                color="blue"
              />
              <StatCard
                icon={<BookOpen className="w-5 h-5" />}
                label="Documents"
                value={studentData.usage_stats?.documents_uploaded || 0}
                color="green"
              />
              <StatCard
                icon={<Award className="w-5 h-5" />}
                label="Certifications"
                value={studentData.usage_stats?.certifications || 0}
                color="purple"
              />
              <StatCard
                icon={<TrendingUp className="w-5 h-5" />}
                label="Points Earned"
                value={studentData.usage_stats?.points || 0}
                color="yellow"
              />
            </div>

            {/* Main Content Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Career Interests */}
              <div className="bg-navy-800/50 rounded-xl p-6 border border-navy-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-blue-400" />
                  Career Interests
                </h3>
                {studentData.student.interests?.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {studentData.student.interests.map((interest, i) => (
                      <span key={i} className="px-3 py-1 bg-blue-600/20 text-blue-300 rounded-full text-sm">
                        {interest}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">No interests selected yet</p>
                )}
                {studentData.student.career_aspirations && (
                  <div className="mt-4 pt-4 border-t border-navy-600">
                    <p className="text-sm text-gray-400">Career Goal:</p>
                    <p className="text-white">{studentData.student.career_aspirations}</p>
                  </div>
                )}
              </div>

              {/* Profile Strength Breakdown */}
              <div className="bg-navy-800/50 rounded-xl p-6 border border-navy-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  Profile Strength Breakdown
                </h3>
                <div className="space-y-3">
                  {Object.entries(studentData.profile_strength?.breakdown || {}).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-gray-300 text-sm capitalize">{key.replace('_', ' ')}</span>
                      <span className="text-green-400 font-semibold">+{value} pts</span>
                    </div>
                  ))}
                  {studentData.profile_strength?.missing?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-navy-600">
                      <p className="text-sm text-yellow-400 mb-2">Missing items:</p>
                      {studentData.profile_strength.missing.slice(0, 3).map((item, i) => (
                        <p key={i} className="text-sm text-gray-400 capitalize">• {item.replace('_', ' ')}</p>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Milestones */}
              <div className="bg-navy-800/50 rounded-xl p-6 border border-navy-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-400" />
                  Milestones & Goals
                </h3>
                {studentData.milestones?.upcoming?.length > 0 ? (
                  <div className="space-y-3">
                    {studentData.milestones.upcoming.slice(0, 5).map((milestone, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-navy-700/50 rounded-lg">
                        <div className="w-8 h-8 bg-purple-600/20 rounded-full flex items-center justify-center">
                          <Clock className="w-4 h-4 text-purple-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium">{milestone.title}</p>
                          <p className="text-gray-400 text-xs">
                            Due: {new Date(milestone.target_date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">No upcoming milestones</p>
                )}
                {studentData.milestones?.completed?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-navy-600">
                    <p className="text-sm text-green-400 mb-2">
                      ✓ {studentData.milestones.completed.length} milestone(s) completed
                    </p>
                  </div>
                )}
              </div>

              {/* Recent Activity */}
              <div className="bg-navy-800/50 rounded-xl p-6 border border-navy-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  Recent Activity
                </h3>
                {studentData.recent_activity?.length > 0 ? (
                  <div className="space-y-3">
                    {studentData.recent_activity.slice(0, 5).map((activity, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                        <span className="text-gray-300 capitalize flex-1">
                          {activity.action_type?.replace('_', ' ')}
                        </span>
                        <span className="text-gray-500 text-xs">
                          {new Date(activity.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">No recent activity</p>
                )}
              </div>
            </div>

            {/* Career Paths Section */}
            {studentData.career_paths?.length > 0 && (
              <div className="mt-6 bg-navy-800/50 rounded-xl p-6 border border-navy-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-cyan-400" />
                  Explored Career Paths
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {studentData.career_paths.slice(0, 6).map((career, i) => (
                    <div key={i} className="p-4 bg-navy-700/50 rounded-lg">
                      <h4 className="font-semibold text-white mb-1">{career.title}</h4>
                      <p className="text-sm text-gray-400">{career.salary_range}</p>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs px-2 py-1 bg-cyan-600/20 text-cyan-300 rounded">
                          {career.match_score}% match
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Academic Portfolio Section */}
            <div className="mt-6 bg-navy-800/50 rounded-xl p-6 border border-navy-700" data-testid="parent-portfolio-section">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-blue-400" />
                Academic Portfolio
              </h3>
              {portfolioData?.access_denied ? (
                <p className="text-gray-400 text-sm">{portfolioData.message}</p>
              ) : portfolioData?.latest_rating ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-blue-400" data-testid="parent-portfolio-score">{portfolioData.latest_rating.overall_score}</div>
                      <div className="text-xs text-gray-400">Profile Score</div>
                    </div>
                    <div className="flex-1 space-y-2">
                      {portfolioData.latest_rating.sub_scores && Object.entries(portfolioData.latest_rating.sub_scores).map(([key, val]) => (
                        <div key={key} className="flex items-center gap-2">
                          <span className="text-xs text-gray-400 w-28 capitalize">{key.replace('_', ' ')}</span>
                          <div className="flex-1 h-2 bg-navy-700 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${val >= 70 ? 'bg-green-500' : val >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${val}%` }} />
                          </div>
                          <span className="text-xs text-gray-300 w-8 text-right">{val}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm text-gray-400">
                    <span>{portfolioData.record_count || 0} records</span>
                    <span>{portfolioData.document_count || 0} documents</span>
                  </div>
                  {/* Score Trend */}
                  {portfolioData.rating_trend?.length > 1 && (
                    <div className="pt-4 border-t border-navy-600">
                      <p className="text-sm text-gray-400 mb-2">Score Trend</p>
                      <div className="flex items-end gap-2 h-16">
                        {portfolioData.rating_trend.map((rt, i) => (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <span className="text-xs text-gray-500">{rt.overall_score}</span>
                            <div className="w-full bg-blue-500/60 rounded-t" style={{ height: `${(rt.overall_score / 100) * 48}px` }} />
                            <span className="text-xs text-slate-300 truncate max-w-full">{rt.grade_level || ''}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Parent Recommendations */}
                  {portfolioData.parent_recommendations?.length > 0 && (
                    <div className="pt-4 border-t border-navy-600">
                      <p className="text-sm text-yellow-400 mb-2 flex items-center gap-1"><Star className="w-4 h-4" /> Recommendations for You</p>
                      <div className="space-y-2">
                        {portfolioData.parent_recommendations.map((rec, i) => (
                          <p key={i} className="text-sm text-gray-300 flex items-start gap-2">
                            <span className="text-yellow-400">•</span> {rec}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Milestones */}
                  {portfolioData.milestones?.length > 0 && (
                    <div className="pt-4 border-t border-navy-600" data-testid="parent-milestones-section">
                      <p className="text-sm text-blue-400 mb-3 flex items-center gap-1"><Award className="w-4 h-4" /> Milestones</p>
                      <div className="flex items-center gap-3 flex-wrap">
                        {portfolioData.milestones.map(ms => (
                          <div key={ms.milestone_key} className="flex items-center gap-2 px-3 py-1.5 rounded-full border"
                            style={{ borderColor: ms.badge_color, backgroundColor: `${ms.badge_color}15` }}
                            data-testid={`parent-milestone-${ms.milestone_key}`}>
                            <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: `${ms.badge_color}30` }}>
                              <BarChart3 className="w-3 h-3" style={{ color: ms.badge_color }} />
                            </div>
                            <span className="text-xs font-medium text-white">{ms.title}</span>
                            <span className="text-xs text-gray-400">({ms.score_at_achievement})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">Your child hasn't built their academic portfolio yet.</p>
                  <p className="text-gray-500 text-xs mt-1">Encourage them to upload transcripts and log their achievements!</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}

function StatCard({ icon, label, value, color }) {
  const colorClasses = {
    blue: 'bg-blue-600/20 text-blue-400',
    green: 'bg-green-600/20 text-green-400',
    purple: 'bg-purple-600/20 text-purple-400',
    yellow: 'bg-yellow-600/20 text-yellow-400',
  };

  return (
    <div className="bg-navy-800/50 rounded-xl p-4 border border-navy-700">
      <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center mb-3`}>
        {icon}
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-gray-400">{label}</p>
    </div>
  );
}
